import React, { useState, useEffect } from "react"
import { INSTITUTES } from "../constants"
import {
  Check,
  User,
  Phone,
  Calendar,
  Clock,
  ChevronRight,
  Mail,
  MapPin,
  Activity,
  ShieldCheck,
  MessageSquare,
  Globe,
  Headphones
} from "lucide-react"

const Booking = () => {
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    institute: "",
    date: "",
    time: "",
    message: ""
  })

  useEffect(() => {
    document.title = "Registry Portal | VCNR Healthcare"
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = e => {
    e.preventDefault()
    setSubmitted(true)
  }

  /* ---------- SUCCESS SCREEN ---------- */
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-16 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-2xl w-full text-center py-16 sm:py-24 border-2 border-vcnr-brand p-6 sm:p-12 bg-white shadow-2xl rounded-[2rem] sm:rounded-[3rem]">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-vcnr-brand text-white rounded-full flex items-center justify-center mx-auto mb-8 sm:mb-10 shadow-xl shadow-vcnr-brand/30 animate-vital-pulse">
            <Check size={32} className="sm:size-[40px]" />
          </div>

          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tighter mb-4 sm:mb-6 text-slate-900">
            Request Sent.
          </h2>

          <p className="text-slate-500 text-xs sm:text-sm font-bold uppercase tracking-widest leading-relaxed mb-8 sm:mb-12 px-2">
            Registry Entry #{Math.floor(Math.random() * 90000) + 10000} <br />
            {formData.name.toUpperCase()}, your clinical slot has been queued. <br />
            A portal agent will contact you at {formData.phone} for verification.
          </p>

          <button
            onClick={() => setSubmitted(false)}
            className="px-10 sm:px-14 py-4 sm:py-5 bg-slate-900 text-white text-[10px] sm:text-[11px] font-black uppercase tracking-[0.25em] sm:tracking-[0.3em] hover:bg-vcnr-brand transition-all rounded-xl sm:rounded-2xl hover:scale-105"
          >
            New Clinical Entry
          </button>
        </div>
      </div>
    )
  }

  /* ---------- MAIN PAGE ---------- */
  return (
    <div className="bg-white min-h-screen">

      {/* -------- HERO BANNER -------- */}
      <section className="relative pt-32 sm:pt-40 md:pt-48 pb-20 sm:pb-28 md:pb-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent z-10"></div>
          <div className="absolute inset-0 hospital-grid opacity-[0.1] z-20"></div>
          <div className="absolute top-0 right-0 w-full h-full opacity-60">
            <img
              src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=2400"
              alt="Clinical Hub"
              className="w-full h-full object-cover grayscale brightness-75"
            />
          </div>
          <div className="scanning-line !h-[2px] !opacity-40 !bg-vcnr-brand"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-30">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 lg:gap-12">

            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white mb-4 sm:mb-6 leading-tight">
                Clinical <br />
                <span className="text-vcnr-brand">Registry.</span>
              </h1>

              <p className="text-slate-200 text-base sm:text-lg mb-6 sm:mb-8">
                Direct access to VCNR Healthcare’s specialized board. Secure
                your medical appointment through our tier-1 logistics portal.
              </p>
            </div>

            <div className="hidden lg:flex flex-col gap-4">
              <div className="p-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl flex items-center gap-5">
                <div className="w-10 h-10 bg-vcnr-brand/20 rounded-xl flex items-center justify-center text-vcnr-brand">
                  <Headphones size={22} />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-slate-400 uppercase mb-1">
                    Live Support
                  </div>
                  <div className="text-lg font-semibold text-white">
                    +91 866 018 2095
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* -------- FORM + CONTACT SECTION -------- */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 relative z-40 -mt-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">

            {/* -------- LEFT CONTACT PANEL -------- */}
            <div className="lg:col-span-5 space-y-8">

              <div className="bg-slate-900 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 text-white shadow-2xl relative overflow-hidden">
                <h3 className="text-xl sm:text-2xl font-semibold mb-8 pb-4 border-b border-white/10 flex items-center gap-3">
                  <MapPin className="text-vcnr-brand" size={22} />
                  Facility Coordinates
                </h3>

                <div className="space-y-8 sm:space-y-12">

                  {/* Address */}
                  <div className="flex gap-5">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-vcnr-brand shrink-0">
                      <MapPin size={22} />
                    </div>
                    <div>
                      <div className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                        VCNR Healthcare
                      </div>
                      <p className="text-slate-200 text-base sm:text-lg">
                        Opp Binnamangala Nursery, B.H. Road,
                        <br />
                        Nelamangala - 562123, Bangalore.
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex gap-5">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-vcnr-brand shrink-0">
                      <Phone size={22} />
                    </div>
                    <div>
                      <div className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                        Primary Hotlines
                      </div>
                      <p className="text-slate-200 text-base sm:text-lg">
                        +91 866 018 2095
                      </p>
                      <p className="text-slate-200 text-base sm:text-lg">
                        +91 916 430 4033
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex gap-5">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-vcnr-brand shrink-0">
                      <Mail size={22} />
                    </div>
                    <div>
                      <div className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                        Email Registry
                      </div>
                      <p className="text-slate-200 text-base sm:text-lg">
                        info@vcnrhealth.com
                      </p>
                    </div>
                  </div>

                </div>
              </div>

              {/* Triage Box */}
              <div className="bg-vcnr-brand/5 border border-vcnr-brand/20 rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldCheck size={18} className="text-vcnr-brand" />
                  <h4 className="font-semibold text-slate-900">
                    Clinical Protocol
                  </h4>
                </div>
                <p className="text-xs text-slate-600 font-medium leading-relaxed italic">
                  All registry submissions are triaged within 60 minutes. For
                  life-threatening emergencies, please bypass the portal and
                  call the hotline immediately.
                </p>
              </div>

            </div>

            {/* -------- RIGHT BOOKING FORM -------- */}
            <div className="lg:col-span-7">
              <div className="bg-white p-6 sm:p-10 md:p-16 border-2 border-slate-900 rounded-[2rem] sm:rounded-[3rem] shadow-[16px_16px_0px_0px_rgba(79,70,229,1)] sm:shadow-[24px_24px_0px_0px_rgba(79,70,229,1)]">

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">

                    {/* Name */}
                    <div className="space-y-2">
                      <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                        <User size={12} /> Patient Identity
                      </label>
                      <input
                        required
                        className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all font-semibold placeholder:text-slate-300"
                        placeholder="FULL LEGAL NAME"
                        value={formData.name}
                        onChange={e =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                        <Phone size={12} /> Contact Vector
                      </label>
                      <input
                        required
                        type="tel"
                        className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all font-semibold placeholder:text-slate-300"
                        placeholder="+91 MOBILE"
                        value={formData.phone}
                        onChange={e =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                      />
                    </div>

                  </div>

                  {/* Institute */}
                  <div className="space-y-2">
                    <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                      <Globe size={12} /> Departmental Hub
                    </label>
                    <div className="relative">
                      <select
                        required
                        className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all text-sm font-semibold appearance-none cursor-pointer"
                        value={formData.institute}
                        onChange={e =>
                          setFormData({ ...formData, institute: e.target.value })
                        }
                      >
                        <option value="">SELECT INSTITUTE UNIT</option>
                        {INSTITUTES.map(inst => (
                          <option key={inst.id} value={inst.name}>
                            {inst.name.toUpperCase()}
                          </option>
                        ))}
                      </select>
                      <ChevronRight
                        size={14}
                        className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none rotate-90"
                      />
                    </div>
                  </div>

                  {/* Date + Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">

                    <div className="space-y-2">
                      <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                        <Calendar size={12} /> Desired Date
                      </label>
                      <input
                        required
                        type="date"
                        className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all text-sm font-bold uppercase"
                        value={formData.date}
                        onChange={e =>
                          setFormData({ ...formData, date: e.target.value })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                        <Clock size={12} /> Registry Time Slot
                      </label>
                      <select
                        required
                        className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all text-sm font-bold uppercase appearance-none cursor-pointer"
                        value={formData.time}
                        onChange={e =>
                          setFormData({ ...formData, time: e.target.value })
                        }
                      >
                        <option value="">SELECT SLOT</option>
                        <option value="morning">Morning (09:00 - 12:00)</option>
                        <option value="afternoon">
                          Afternoon (13:00 - 16:00)
                        </option>
                        <option value="evening">Evening (17:00 - 20:00)</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-[11px] sm:text-[12px] font-semibold uppercase text-vcnr-brand flex items-center gap-2">
                      <MessageSquare size={12} /> Clinical Notes / Message
                    </label>
                    <textarea
                      className="w-full bg-slate-50 border border-slate-100 px-5 sm:px-6 py-3 sm:py-4 rounded-xl focus:ring-2 focus:ring-vcnr-brand/20 focus:border-vcnr-brand focus:outline-none transition-all text-sm font-bold uppercase placeholder:text-slate-300 min-h-[120px]"
                      placeholder="STATE SYMPTOMS OR CLINICAL REQUIREMENTS..."
                      value={formData.message}
                      onChange={e =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="w-full py-5 sm:py-6 bg-slate-900 text-white text-base sm:text-lg font-semibold hover:bg-vcnr-brand transition-all flex items-center justify-center group shadow-xl active:scale-[0.98] rounded-xl sm:rounded-2xl"
                  >
                    Send Enquiry
                    <ChevronRight
                      size={16}
                      className="ml-2 group-hover:translate-x-2 transition-transform"
                    />
                  </button>

                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* -------- MAP SECTION -------- */}
      <section className="py-0 px-0">
        <div className="w-full h-[350px] sm:h-[450px] md:h-[500px] grayscale brightness-90 hover:grayscale-0 hover:brightness-100 transition-all duration-[2s]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.0238028893446!2d77.39289587507861!3d13.097677987229384!2m3!1f0!2f0!2f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae25c200ee3f65%3A0xd9cebe53315764eb!2sVCNR%20Hospital!5e0!3m2!1sen!2sin!4v1770105302400!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="VCNR Hospital Location"
          ></iframe>
        </div>

        <div className="bg-slate-900 py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-vcnr-brand/20 rounded-xl flex items-center justify-center text-vcnr-brand">
                <MapPin size={22} />
              </div>
              <div>
                <div className="text-[11px] sm:text-[12px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                  Facility Address
                </div>
                <div className="text-white uppercase text-sm sm:text-base">
                  Opp Binnamangala Nursery, Nelamangala - 562123
                </div>
              </div>
            </div>

            <a
              href="https://maps.google.com/?q=VCNR+Hospital+Nelamangala"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 sm:px-8 py-3 bg-white text-slate-900 rounded-xl text-[11px] sm:text-[12px] font-bold uppercase hover:bg-vcnr-brand hover:text-white transition-all"
            >
              Get Precise Directions
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}

export default Booking
